import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { SessionState } from '../../features/auth/state/session.state';
import { ToastService } from '../../shared/services/toast.service';

const ERROR_MESSAGES: Record<number, string> = {
  0:   'No hay conexión disponible.',
  401: 'Tu sesión expiró. Inicia sesión nuevamente.',
  403: 'No tienes permisos para realizar esta acción.',
};

const SERVER_ERROR_MESSAGE = 'Error en el servidor. Intenta más tarde.';
const FALLBACK_MESSAGE     = 'Lo sentimos, no se pudo completar la acción.';

/** Rutas que no deben disparar el cierre de sesión automático en un 401. */
const AUTH_PATHS_EXCLUDED_FROM_LOGOUT = ['/session/login', '/logout'];

function resolveMessage(error: HttpErrorResponse): string {
  // Códigos exactos con mensaje fijo (0, 401, 403)
  if (ERROR_MESSAGES[error.status] !== undefined) {
    return ERROR_MESSAGES[error.status];
  }

  // Rango servidor: siempre mensaje genérico (nunca exponer detalles del servidor)
  if (error.status >= 500) {
    return SERVER_ERROR_MESSAGE;
  }

  // 4xx restantes (400, 404, 409, 422…): preferir mensaje del backend si es legible
  const backendMessage = error.error?.message;
  if (backendMessage && typeof backendMessage === 'string' && backendMessage.length <= 200) {
    return backendMessage;
  }

  return FALLBACK_MESSAGE;
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const session = inject(SessionState);
  const toast   = inject(ToastService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      const isAuthPath = AUTH_PATHS_EXCLUDED_FROM_LOGOUT.some(path =>
        new URL(req.url, window.location.origin).pathname.endsWith(path)
      );

      if (error.status === 401 && !isAuthPath) {
        session.expireLocalSession();
      }

      toast.showError(resolveMessage(error));

      return throwError(() => error);
    })
  );
};
